import { createContext, useContext, useState, ReactNode } from 'react';
import { MockPlace, MockProperty, MOCK_PLACES, MOCK_PROPERTY_DEFAULT } from './vsj-mocks';
import { PlaceReadingDto } from './vsj-read-api';

export type VsjStep = 'home' | 'location' | 'environment' | 'property' | 'result';

/** Choreography state for the reading moment, shared with the map stage. */
export type VsjEnvPhase = 'idle' | 'reading' | 'read';

/** Result of calling places/locate — stored so back-navigation skips the re-fetch. */
export interface VsjLocateResult {
  placeToken: string;
  confirmedGeometry: object | null;
  streetName: string | null;
  referenceLabel: string | null;
}

interface VsjFlowState {
  step: VsjStep;
  searchQuery: string;
  place: MockPlace;
  property: MockProperty;
  envPhase: VsjEnvPhase;
  /** True once the environment has been read for the current place. */
  envRead: boolean;
  /** Set after a successful places/locate call; null until confirmed. */
  locateResult: VsjLocateResult | null;
  /** Set after a successful places/read call; null until the ceremony completes. */
  readingResult: PlaceReadingDto | null;
  /** Set after a successful POST /valuations; null until session is created. */
  sessionToken: string | null;
  /** Set after a successful POST /valuations/:token/report; null until report is created. */
  reportToken: string | null;
  goTo: (step: VsjStep) => void;
  setSearchQuery: (v: string) => void;
  selectPlace: (p: MockPlace) => void;
  setProperty: (v: Partial<MockProperty>) => void;
  setEnvPhase: (p: VsjEnvPhase) => void;
  setEnvRead: (v: boolean) => void;
  setLocateResult: (r: VsjLocateResult | null) => void;
  setReadingResult: (r: PlaceReadingDto | null) => void;
  setSessionToken: (t: string | null) => void;
  setReportToken: (t: string | null) => void;
}

const VsjFlowContext = createContext<VsjFlowState | null>(null);

interface VsjFlowProviderProps {
  children: ReactNode;
  initialPlace?: MockPlace;
  initialStep?: VsjStep;
}

export function VsjFlowProvider({ children, initialPlace, initialStep }: VsjFlowProviderProps) {
  const [step, setStep] = useState<VsjStep>(initialStep ?? 'home');
  const [searchQuery, setSearchQuery] = useState(initialPlace?.address ?? '');
  const [place, setPlace] = useState<MockPlace>(initialPlace ?? MOCK_PLACES[0]);
  const [property, setPropertyState] = useState<MockProperty>(MOCK_PROPERTY_DEFAULT);
  const [envPhase, setEnvPhase] = useState<VsjEnvPhase>('idle');
  const [envRead, setEnvRead] = useState(false);
  const [locateResult, setLocateResult] = useState<VsjLocateResult | null>(null);
  const [readingResult, setReadingResult] = useState<PlaceReadingDto | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [reportToken, setReportToken] = useState<string | null>(null);

  const goTo = (s: VsjStep) => setStep(s);

  const selectPlace = (p: MockPlace) => {
    setPlace(p);
    setEnvRead(false); // new place → environment must be read again
    setLocateResult(null); // new place → locate must be re-fetched
    setReadingResult(null); // new place → reading must be re-fetched
    setSessionToken(null); // new place → session must be recreated
    setReportToken(null); // new place → report must be recreated
  };

  const setProperty = (v: Partial<MockProperty>) => setPropertyState(prev => ({ ...prev, ...v }));

  return (
    <VsjFlowContext.Provider
      value={{
        step,
        searchQuery,
        place,
        property,
        envPhase,
        envRead,
        locateResult,
        readingResult,
        sessionToken,
        reportToken,
        goTo,
        setSearchQuery,
        selectPlace,
        setProperty,
        setEnvPhase,
        setEnvRead,
        setLocateResult,
        setReadingResult,
        setSessionToken,
        setReportToken,
      }}
    >
      {children}
    </VsjFlowContext.Provider>
  );
}

export function useVsjFlow(): VsjFlowState {
  const ctx = useContext(VsjFlowContext);
  if (!ctx) throw new Error('useVsjFlow must be used inside VsjFlowProvider');
  return ctx;
}
